import type { ProjectTask, TaskComment } from '../types';
import { taskInvolvesUser } from './projectTaskUsers';

export type TaskCommentNotifyShape = Pick<
  TaskComment,
  'authorId' | 'mentions' | 'notifyUserIds' | 'taskId'
>;

/**
 * Mirrors `onTaskCommentCreated` recipient targeting: assignees, action-required,
 * @mentions, and group `notifyUserIds` (author excluded).
 */
export function userIsTaskCommentNotifyTarget(
  comment: TaskCommentNotifyShape,
  userId: string,
  projectTasks: ProjectTask[],
): boolean {
  const uid = (userId || '').trim();
  if (!uid) return false;
  if (comment.authorId && comment.authorId === uid) return false;

  if (Array.isArray(comment.mentions)) {
    for (const m of comment.mentions) {
      if (m?.userId && m.userId === uid) return true;
    }
  }

  if (Array.isArray(comment.notifyUserIds)) {
    for (const id of comment.notifyUserIds) {
      if (id && String(id) === uid) return true;
    }
  }

  const taskId = (comment.taskId || '').trim();
  if (taskId) {
    const task = projectTasks.find((t) => t.id === taskId);
    if (task && taskInvolvesUser(task, uid)) return true;
  }

  return false;
}

/** Build Project Hub path for task/group comment deep links (client navigation). */
export function buildProjectHubCommentDeepLinkPath(
  projectId: string,
  options?: { taskId?: string; taskCommentId?: string; groupKey?: string; discussion?: string },
): string {
  const pid = (projectId || '').trim();
  if (!pid) return '/project-hub';
  const q = new URLSearchParams();
  if (options?.groupKey?.trim()) {
    q.set('group', options.groupKey.trim());
  } else if (options?.taskId?.trim()) {
    q.set('task', options.taskId.trim());
  }
  if (options?.taskCommentId?.trim()) {
    q.set('taskComment', options.taskCommentId.trim());
  }
  const discussion = (options?.discussion || '').trim();
  if (discussion === 'architect' || discussion === 'comments' || discussion === 'pdf') {
    q.set('discussion', discussion);
  }
  const qs = q.toString();
  return qs ? `/project-hub/${pid}?${qs}` : `/project-hub/${pid}`;
}
